import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Reveal from "@/components/Reveal";
import Eyebrow from "@/components/Eyebrow";
import FullBleedImage from "@/components/FullBleedImage";
import { IMG } from "@/lib/images";

const PILLARS = [
  {
    number: "01",
    title: "Website Design",
    lead:
      "We build clean, professional websites from the ground up — tailored to your business, optimized for search, and designed to turn visitors into customers.",
    image: IMG.estateArch,
    deliverables: [
      "Custom design tailored to your brand",
      "Mobile-friendly and fast on every device",
      "SEO optimized so customers can find you",
      "Contact forms, menus, booking links — whatever you need",
      "Fully handed off and ready to launch",
    ],
  },
  {
    number: "02",
    title: "Hosting & Maintenance",
    lead:
      "We keep your site running, updated, protected, and looking sharp every month — so you never have to think about it.",
    image: IMG.barrelRoom,
    deliverables: [
      "Managed hosting with 99.9% uptime",
      "Monthly content updates on request",
      "Security monitoring and regular backups",
      "Performance checks to keep your site fast",
      "Direct access to us whenever you need something",
    ],
  },
  {
    number: "03",
    title: "Social Media Marketing",
    lead:
      "We create and manage content that gets your business in front of the right people — photography-first storytelling, consistent posting, and real results you can measure.",
    image: IMG.vineyardDusk,
    deliverables: [
      "Custom content strategy for your business",
      "Photo and video content creation",
      "Captions, hashtags, and posting schedule",
      "Community management and comment responses",
      "Monthly performance report",
    ],
  },
];

/*const PACKAGES = [
  {
    tier: "Foundation",
    intro:
      "For boutique operators who need a refined, single-page web presence and a light social cadence.",
    items: [
      "Single-page editorial website",
      "Hosting & quarterly care",
      "Monthly social direction",
    ],
  },
  {
    tier: "Estate",
    intro:
      "For wineries, hotels, and lifestyle brands ready for a full site and sustained storytelling.",
    items: [
      "Multi-page website with CMS",
      "Hosting, maintenance & content updates",
      "Bi-weekly social content program",
      "Seasonal creative direction",
    ],
    featured: true,
  },
  {
    tier: "Reserve",
    intro:
      "For established brands that want a studio on retainer — thinking with them across every surface.",
    items: [
      "Custom website & brand system",
      "Priority maintenance & uptime",
      "Always-on social program",
      "Photography direction & shoots",
      "Quarterly strategy reviews",
    ],
  },
];*/

const FAQ = [
  {
    q: "How long does it take to build a website?",
    a: "Most websites are completed within one week from our first conversation. We move quickly and keep you updated throughout so there are no surprises.",
  },
  {
    q: "What does it cost?",
    a: "Website builds start at $1200. Hosting and maintenance starts at $125/month. Social media management is custom quoted based on your needs. We're always happy to talk through what makes sense for your budget.",
  },
  {
    q: "Do I own my website when it's done?",
    a: "Yes — completely. The domain, the content, and everything on it is yours. We just keep it running and looking sharp each month.",
  },
  {
    q: "What if I already have a website but want to improve it?",
    a: "We can work with what you have or rebuild from scratch depending on what makes more sense. Either way we'll give you an honest recommendation after taking a look.",
  },
  {
    q: "Do you only work with businesses in San Luis Obispo?",
    a: "We're based in SLO and love working with local businesses, but we're happy to work with anyone who's a good fit. Reach out and let's talk.",
  },
];

export default function Services() {
  useEffect(() => {
    document.title = "Services — Bishop Creative";
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative pt-28 md:pt-40 pb-16 md:pb-20" style={{ background: "var(--bg-0)" }}>
        <div className="container-page">
          <Reveal>
            <Eyebrow>Services</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-8 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-[-0.02em] max-w-[18ch]">
              Digital media built for local business.
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-8 max-w-[60ch] text-[color:var(--text-2)] text-lg leading-[1.7]">
              We build websites, manage social media, and help local businesses show up online the way they deserve to.
              Every project is personal — because we're local students who care about this community.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="container-page">
        <div className="hairline-strong" />
      </div>

      {/* PILLARS */}
      <section className="section-pad">
        <div className="container-page space-y-24 md:space-y-36">
          {PILLARS.map((p, i) => (
            <article
              key={p.number}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
              data-testid={`services-pillar-${i}`}
            >
              <div
                className={`lg:col-span-6 ${
                  i % 2 === 1 ? "lg:order-2" : ""
                }`}
              >
                <Reveal>
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div
                      aria-hidden
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(11,10,8,0.10) 0%, rgba(11,10,8,0.55) 100%)",
                      }}
                    />
                    <div className="noise-overlay" />
                  </div>
                </Reveal>
              </div>
              <div
                className={`lg:col-span-6 lg:pt-8 ${
                  i % 2 === 1 ? "lg:order-1" : ""
                }`}
              >
                <Reveal>
                  <div className="eyebrow text-[color:var(--text-3)]">
                    {p.number} — Service
                  </div>
                </Reveal>
                <Reveal delay={0.08}>
                  <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
                    {p.title}
                  </h2>
                </Reveal>
                <Reveal delay={0.14}>
                  <p className="mt-6 text-[color:var(--text-2)] leading-[1.8] max-w-[52ch]">
                    {p.lead}
                  </p>
                </Reveal>
                <Reveal delay={0.2}>
                  <div className="mt-10 hairline" />
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {p.deliverables.map((d) => (
                      <li
                        key={d}
                        className="flex items-start gap-3 text-sm text-[color:var(--text-2)]"
                      >
                        <span
                          aria-hidden
                          className="mt-2 h-px w-4 shrink-0"
                          style={{ background: "rgba(198,168,106,0.65)" }}
                        />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* PACKAGES
      <section className="section-pad" style={{ background: "var(--bg-1)" }}>
        <div className="container-page">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Reveal>
                <Eyebrow>Packaged engagements</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
                  Three ways to begin.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <p className="max-w-[46ch] text-[color:var(--text-2)] leading-[1.75]">
                Most engagements begin with one of these and shape themselves to
                fit. Pricing shared in a tailored proposal.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {PACKAGES.map((pkg, i) => (
              <Reveal key={pkg.tier} delay={i * 0.08}>
                <div
                  className={`relative h-full p-8 md:p-10 border ${
                    pkg.featured
                      ? "border-[color:rgba(198,168,106,0.45)]"
                      : "border-[color:var(--hairline)]"
                  }`}
                  style={{ background: "rgba(20,17,14,0.65)" }}
                  data-testid={`services-package-${pkg.tier.toLowerCase()}`}
                >
                  {pkg.featured && (
                    <div
                      className="absolute -top-3 left-8 px-3 py-1 eyebrow"
                      style={{
                        background: "var(--brass)",
                        color: "var(--text-inverse)",
                        letterSpacing: "0.24em",
                      }}
                    >
                      Most chosen
                    </div>
                  )}
                  <div className="eyebrow text-[color:var(--text-3)]">
                    0{i + 1}
                  </div>
                  <h3 className="mt-4 font-display text-3xl md:text-4xl text-[color:var(--text-1)]">
                    {pkg.tier}
                  </h3>
                  <p className="mt-4 text-[color:var(--text-2)] leading-[1.7] max-w-[34ch]">
                    {pkg.intro}
                  </p>
                  <div className="hairline mt-8" />
                  <ul className="mt-6 space-y-3">
                    {pkg.items.map((it) => (
                      <li
                        key={it}
                        className="flex items-start gap-3 text-sm text-[color:var(--text-2)]"
                      >
                        <span
                          aria-hidden
                          className="mt-2 h-px w-4 shrink-0"
                          style={{ background: "rgba(198,168,106,0.65)" }}
                        />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ */}
      <section className="section-pad">
        <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <Eyebrow>Frequently asked</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
                The practical details.
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-6 text-[color:var(--text-2)] max-w-[40ch] leading-[1.75]">
                If you don’t see your question, write to us. We reply within
                24 hours.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="w-full">
                {FAQ.map((f, i) => (
                  <AccordionItem
                    key={f.q}
                    value={`item-${i}`}
                    className="border-b border-[color:var(--hairline)]"
                  >
                    <AccordionTrigger
                      className="font-display text-xl md:text-2xl text-left text-[color:var(--text-1)] hover:no-underline py-6"
                      data-testid={`services-faq-${i}`}
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[color:var(--text-2)] leading-[1.8] max-w-[62ch] pb-8">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA full bleed */}
      <section className="relative">
        <FullBleedImage
          src={IMG.goldenHills}
          alt="Golden hills of the Central Coast"
          height="h-[70vh] min-h-[480px]"
        >
          <div className="container-page h-full flex flex-col justify-center">
            <Reveal>
              <Eyebrow>Begin</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] max-w-[18ch]">
                Ready to take your business to the next level online?
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-10">
                <Link
                  to="/contact"
                  className="btn btn-primary"
                  data-testid="services-cta"
                >
                  Start a Project
                </Link>
              </div>
            </Reveal>
          </div>
        </FullBleedImage>
      </section>
    </div>
  );
}
